import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";

import {
  districtLanguageOf,
  districtNames,
  HkDistrictLanguage,
  translateDistrict,
} from "../../../constants/hkDistricts";
import { updateUserAddress } from "../../../services/userServices";
import { UserType } from "../../../types/user";

import styles from "./index.module.scss";

interface IUpdateAddressModal {
  user: UserType;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type AddressField =
  | "addr_line_1"
  | "addr_line_2"
  | "addr_line_3"
  | "addr_postal";

type AddressFieldConfig = {
  name: AddressField;
  label: string;
  required: boolean;
  /**
   * Hong Kong bilingual district list. Only the HK field config sets this.
   * Another country must not reuse it: the radios translate HK_DISTRICTS.
   */
  district?: boolean;
};

const defaultFields: AddressFieldConfig[] = [
  { name: "addr_line_1", label: "Address line 1", required: true },
  { name: "addr_line_2", label: "Address line 2", required: false },
  { name: "addr_line_3", label: "Address line 3", required: false },
  { name: "addr_postal", label: "Postal code", required: false },
];

const fieldsByCountry: Record<string, AddressFieldConfig[]> = {
  SG: [
    {
      name: "addr_line_1",
      label: "House/building number with street name",
      required: true,
    },
    {
      name: "addr_line_2",
      label: "Floor-unit (optional for landed)",
      required: false,
    },
    { name: "addr_line_3", label: "Building name (optional)", required: false },
    { name: "addr_postal", label: "Postal code", required: false },
  ],
  HK: [
    { name: "addr_line_1", label: "Unit, floor, building", required: true },
    { name: "addr_line_2", label: "Street address", required: false },
    {
      name: "addr_line_3",
      label: "District",
      required: false,
      district: true,
    },
    { name: "addr_postal", label: "Postal code", required: false },
  ],
};

const fieldsForUser = (user: UserType): AddressFieldConfig[] =>
  fieldsByCountry[user.app_country?.trim().toUpperCase() || ""] ||
  defaultFields;

const addressFromUser = (user: UserType): Record<AddressField, string> => {
  const address: Record<AddressField, string> = {
    addr_line_1: user.addr_line_1 || "",
    addr_line_2: user.addr_line_2 || "",
    addr_line_3: user.addr_line_3 || "",
    addr_postal: user.addr_postal || "",
  };
  const district = fieldsForUser(user).find((field) => field.district);
  if (district) {
    const stored = address[district.name];
    address[district.name] = translateDistrict(
      stored,
      districtLanguageOf(stored)
    );
  }
  return address;
};

const districtLanguageFromUser = (user: UserType): HkDistrictLanguage => {
  const district = fieldsForUser(user).find((field) => field.district);
  return districtLanguageOf(
    district ? addressFromUser(user)[district.name] : ""
  );
};

export default function UpdateAddressModal({
  user,
  onClose,
  onSuccess,
  isVisible = false,
}: IUpdateAddressModal) {
  const { userId } = useParams();
  const fields = fieldsForUser(user);
  const [address, setAddress] = useState(addressFromUser(user));
  const [districtLanguage, setDistrictLanguage] = useState<HkDistrictLanguage>(
    () => districtLanguageFromUser(user)
  );

  useEffect(() => {
    if (isVisible) {
      setAddress(addressFromUser(user));
      setDistrictLanguage(districtLanguageFromUser(user));
    }
  }, [isVisible, user]);

  const updateMutation = useMutation({
    mutationFn: () => updateUserAddress(userId || "", address),
    onSuccess: () => {
      toast.success("Address updated successfully");
      onSuccess();
      onClose();
    },
    onError: (error: Error) => {
      toast.error(
        `An error happened. Please reach out to the engineering team with the following data: error: ${error.message}, userId: ${userId}`
      );
    },
  });

  const handleChange =
    (field: AddressField) => (event: { target: { value: string } }) => {
      setAddress((current) => ({ ...current, [field]: event.target.value }));
    };

  const handleDistrictLanguage = (
    field: AddressField,
    language: HkDistrictLanguage
  ) => {
    setDistrictLanguage(language);
    setAddress((current) => ({
      ...current,
      [field]: translateDistrict(current[field], language),
    }));
  };

  const handleSubmit = () => {
    const line1 = fields.find((field) => field.name === "addr_line_1");
    if (!address.addr_line_1.trim()) {
      toast.error(`${line1?.label || "Address line 1"} is required`);
      return;
    }
    updateMutation.mutate();
  };

  return (
    <Modal
      id="updateAddressModal"
      aria-labelledby="update-address-modal-title"
      centered
      show={isVisible}
      onHide={onClose}
    >
      <Modal.Body>
        <div className={styles.modalContainer}>
          <h2 id="update-address-modal-title" className={styles.modalTitle}>
            Update address
          </h2>
          <p className={styles.modalDescription}>
            Changes are saved for this customer and recorded in the audit
            history.
          </p>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="addr_country">Country</Form.Label>
            <Form.Control
              id="addr_country"
              type="text"
              value={user.addr_country || ""}
              readOnly
              disabled
            />
          </Form.Group>
          {fields.map((field) => {
            const knownOptions = field.district
              ? districtNames(districtLanguage)
              : undefined;
            const options = knownOptions
              ? [
                  ...(address[field.name] &&
                  !knownOptions.includes(address[field.name])
                    ? [address[field.name]]
                    : []),
                  ...knownOptions,
                ]
              : undefined;

            return (
              <Form.Group className="mb-3" key={field.name}>
                <Form.Label htmlFor={field.name}>{field.label}</Form.Label>
                {options ? (
                  <>
                    <div className={styles.districtLanguage}>
                      <Form.Check
                        inline
                        type="radio"
                        name="district-language"
                        id="district-language-en"
                        label="English"
                        checked={districtLanguage === "en"}
                        onChange={() =>
                          handleDistrictLanguage(field.name, "en")
                        }
                      />
                      <Form.Check
                        inline
                        type="radio"
                        name="district-language"
                        id="district-language-zh-Hant"
                        label="Traditional Chinese"
                        checked={districtLanguage === "zh-Hant"}
                        onChange={() =>
                          handleDistrictLanguage(field.name, "zh-Hant")
                        }
                      />
                    </div>
                    <Form.Select
                      id={field.name}
                      value={address[field.name]}
                      onChange={handleChange(field.name)}
                      required={field.required}
                    >
                      <option value=""></option>
                      {options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                  </>
                ) : (
                  <Form.Control
                    id={field.name}
                    type="text"
                    value={address[field.name]}
                    onChange={handleChange(field.name)}
                    required={field.required}
                  />
                )}
              </Form.Group>
            );
          })}
          <div className={styles.modalFooter}>
            <Button
              className={styles.submitButton}
              variant="success"
              size="lg"
              onClick={handleSubmit}
              disabled={updateMutation.isLoading}
            >
              {updateMutation.isLoading ? "Processing..." : "Update"}
            </Button>
            <Button
              className={styles.cancelButton}
              variant="secondary"
              size="lg"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
